<template>
  <div class="exercises-page">
    <div v-if="state === 'loading'" class="exercises-state" role="status">
      <span class="exercises-state__spinner" aria-hidden="true"></span>
      <p>Preparando tus ejercicios…</p>
    </div>
    <ToolAccessState v-else-if="state === 'restricted'" title="Ejercicios no incluidos" message="Tu plan actual no incluye acceso a los ejercicios." />
    <section v-else-if="state === 'error'" class="exercises-state" role="alert">
      <h1>No pudimos cargar tus ejercicios</h1>
      <p>{{ errorMessage }}</p>
      <button type="button" @click="load">Reintentar</button>
    </section>
    <section v-else-if="!catalog.sections.length" class="exercises-state">
      <h1>Aún no hay ejercicios disponibles</h1>
      <p>Vuelve más adelante para comenzar tu práctica.</p>
    </section>
    <template v-else>
      <header class="exercises-hero">
        <div>
          <span class="exercises-hero__eyebrow">Práctica guiada</span>
          <h1>Ejercicios</h1>
          <p>Entrena el método Eurekers a tu ritmo y continúa siempre donde lo dejaste.</p>
        </div>
        <div class="exercises-hero__progress" :aria-label="`Progreso total: ${catalog.summary.progressPercent}%`">
          <strong>{{ catalog.summary.progressPercent }}%</strong>
          <span>Progreso total</span>
        </div>
      </header>

      <section v-if="catalog.summary.next" class="exercise-resume" aria-labelledby="exercise-resume-title">
        <div class="exercise-resume__copy">
          <span>Tu siguiente paso</span>
          <h2 id="exercise-resume-title">{{ catalog.summary.next.title }}</h2>
          <p>Pregunta {{ catalog.summary.next.position + 1 }}. Puedes retomarla ahora o explorar el recorrido completo.</p>
        </div>
        <button type="button" @click="openReference(catalog.summary.next)">Continuar practicando <span aria-hidden="true">→</span></button>
      </section>

      <nav class="exercise-section-tabs" aria-label="Bloques de ejercicios">
        <button v-for="section in catalog.sections" :key="section.id" type="button" :class="{ 'is-active': section.id === activeSection }" @click="activeSection = section.id">
          {{ section.title }}
        </button>
      </nav>

      <section v-for="section in visibleSections" :key="section.id" class="exercise-section" :aria-labelledby="`section-${section.id}`">
        <header>
          <div>
            <h2 :id="`section-${section.id}`">{{ section.title }}</h2>
            <p>{{ section.description }}</p>
          </div>
          <span>{{ section.items.filter(item => item.completed).length }} de {{ section.items.length }} bloques</span>
        </header>
        <div class="exercise-section__grid">
          <ExerciseCatalogCard v-for="item in section.items" :key="item.id" :item="item" @open="openItem" />
        </div>
      </section>
    </template>
  </div>
</template>

<script>
import ToolAccessState from '@/shared/components/common/ToolAccessState.vue';
import ExerciseCatalogCard from '../components/ExerciseCatalogCard.vue';
import { exercisesRepository } from '../api/exercisesRepository';

export default {
  name: 'ExercisesNextPage',
  components: { ToolAccessState, ExerciseCatalogCard },
  data: () => ({
    state: 'loading',
    errorMessage: '',
    catalog: { summary: { progressPercent: 0, next: null }, sections: [] },
    activeSection: ''
  }),
  computed: {
    visibleSections() {
      return this.catalog.sections.filter(section => !this.activeSection || section.id === this.activeSection);
    }
  },
  created() { this.load(); },
  methods: {
    async load() {
      this.state = 'loading';
      this.errorMessage = '';
      try {
        this.catalog = await exercisesRepository.getCatalog();
        this.activeSection = this.catalog.sections[0] ? this.catalog.sections[0].id : '';
        this.state = 'ready';
      } catch (error) {
        if (error && error.status === 403) this.state = 'restricted';
        else {
          this.state = 'error';
          this.errorMessage = error && error.message ? error.message : 'Comprueba tu conexión e inténtalo de nuevo.';
        }
      }
    },
    openReference(reference, reset = false) {
      this.$router.push({
        name: 'ExercisesNextSession',
        params: { type: reference.type, subtype: reference.subtype },
        query: { position: reset ? 0 : reference.position, ...(reset ? { reset: '1' } : {}) }
      });
    },
    openItem(item, reset) {
      this.openReference({ ...item, position: reset ? 0 : item.currentPosition }, reset);
    }
  }
};
</script>

<style lang="scss" scoped>
.exercises-page { height: 100%; min-height: 0; box-sizing: border-box; overflow-y: auto; padding: 22px 16px 56px; font-family: 'Open Sans', sans-serif; color: var(--modern-text); }
.exercises-page > * { width: min(1180px, 100%); margin-right: auto; margin-left: auto; }
.exercises-hero { display: flex; align-items: center; justify-content: space-between; gap: 24px; padding: 8px 0 22px; }
.exercises-hero__eyebrow { color: var(--modern-accent); font-size: 12px; font-weight: 800; letter-spacing: .1em; text-transform: uppercase; }
.exercises-hero h1 { margin: 5px 0 6px; font-size: clamp(30px, 6vw, 46px); letter-spacing: -.04em; }
.exercises-hero p { max-width: 620px; margin: 0; color: var(--modern-muted); line-height: 1.5; }
.exercises-hero__progress { display: grid; place-items: center; flex: 0 0 116px; aspect-ratio: 1; border: 9px solid var(--modern-accent-soft); border-top-color: var(--modern-accent); border-radius: 50%; }
.exercises-hero__progress strong { font-size: 24px; line-height: 1; }
.exercises-hero__progress span { color: var(--modern-muted); font-size: 10px; }
.exercise-resume { display: flex; align-items: center; justify-content: space-between; gap: 22px; padding: 20px; border-radius: 20px; background: linear-gradient(125deg, #087fa2, #00a7cf); color: #fff; box-shadow: 0 14px 35px rgba(0, 141, 181, .22); }
.exercise-resume__copy span { font-size: 11px; font-weight: 800; letter-spacing: .08em; text-transform: uppercase; opacity: .78; }
.exercise-resume h2 { margin: 4px 0; font-size: 22px; }
.exercise-resume p { margin: 0; font-size: 13px; opacity: .82; }
.exercise-resume button { min-height: 48px; flex: 0 0 auto; padding: 0 18px; border: 0; border-radius: 13px; background: #fff; color: #087fa2; font: inherit; font-weight: 800; cursor: pointer; }
.exercise-section-tabs { display: flex; gap: 8px; margin-top: 28px; overflow-x: auto; padding-bottom: 4px; scrollbar-width: thin; }
.exercise-section-tabs button { min-height: 44px; flex: 0 0 auto; padding: 0 16px; border: 1px solid var(--modern-border); border-radius: 12px; background: var(--modern-surface); color: var(--modern-muted); font: inherit; cursor: pointer; }
.exercise-section-tabs button.is-active { border-color: var(--modern-accent); background: var(--modern-accent-soft); color: var(--modern-accent); font-weight: 800; }
.exercise-section { margin-top: 26px; }
.exercise-section > header { display: flex; justify-content: space-between; align-items: end; gap: 16px; margin-bottom: 14px; }
.exercise-section h2 { margin: 0 0 3px; font-size: 23px; }
.exercise-section header p, .exercise-section header > span { margin: 0; color: var(--modern-muted); font-size: 13px; }
.exercise-section__grid { display: grid; grid-template-columns: 1fr; gap: 14px; }
.exercises-state { display: grid; place-items: center; align-content: center; min-height: 60vh; text-align: center; }
.exercises-state h1 { margin-bottom: 4px; }
.exercises-state p { color: var(--modern-muted); }
.exercises-state button { min-height: 44px; padding: 0 18px; border: 0; border-radius: 11px; background: var(--modern-accent); color: #fff; font: inherit; font-weight: 700; cursor: pointer; }
.exercises-state__spinner { width: 32px; height: 32px; border: 3px solid var(--modern-border); border-top-color: var(--modern-accent); border-radius: 50%; animation: exercise-spin .8s linear infinite; }
@keyframes exercise-spin { to { transform: rotate(360deg); } }
@media (min-width: 680px) { .exercises-page { padding: 30px 24px 64px; } .exercise-section__grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (min-width: 1040px) { .exercise-section__grid { grid-template-columns: repeat(3, minmax(0, 1fr)); } }
@media (max-width: 600px) { .exercises-hero { align-items: flex-start; } .exercises-hero__progress { flex-basis: 90px; border-width: 7px; } .exercise-resume { display: grid; } .exercise-resume button { width: 100%; } }
</style>
